import type { CalendarQuery } from '@complan/contracts';
import type { TFunction } from 'i18next';
import {
  BOOLEAN_KEYS,
  LIST_KEYS,
  type BooleanFilterKey,
  type ListFilterKey,
} from '../../shared/url/calendar-search-params';
import { i18n } from '../../shared/i18n';

/**
 * The filter-chip row of UI spec §3.7 as pure data.
 *
 * The chip row is not decoration: it is the visual surface of R11.9's result
 * count, so it terminates with a **mandatory** result-context chip that is present
 * even when no filter is applied and even when the count is zero. `deriveChips`
 * therefore always returns at least one chip, and that last chip is always the
 * result-count chip (design.md, "Filters and Saved Views"; UI spec §3.7).
 *
 * Keeping the derivation pure — a function of the parsed query, a label lookup and
 * the count — is what lets a unit test pin the mandatory-chip and zero-count
 * behaviour without a DOM, and it keeps the single-source-of-truth invariant
 * honest: the chips describe exactly the state the URL holds, nothing more.
 */

/** One rendered filter chip. */
export interface FilterChip {
  /** A stable key for React and for the remove control. */
  readonly key: string;
  /** The label shown on the chip, e.g. `Platform: Consumer Care` or `Confidential: yes`. */
  readonly label: string;
  /** A removable filter chip carries the dimension (and value) its remove control clears. */
  readonly removal?: ChipRemoval;
  /**
   * The mandatory result-context chip that terminates the row (R11.9). It is not
   * removable and is styled as a count rather than a filter.
   */
  readonly kind: 'filter' | 'result';
}

/**
 * What a chip's remove control clears: a whole Boolean dimension, one value of a
 * list dimension, or the free-text Search term.
 */
export type ChipRemoval =
  | { readonly type: 'boolean'; readonly key: BooleanFilterKey }
  | { readonly type: 'list-value'; readonly key: ListFilterKey; readonly value: string }
  | { readonly type: 'search' };

/**
 * Resolves a stored identifier to the human name a chip shows. Returns
 * `undefined` when no name is known, in which case the chip falls back to the raw
 * value — a Saved_View may still carry a value whose option list the screen has
 * not loaded (or, per the flagged API gap, cannot load), and a chip must remain
 * visible and removable rather than vanish (R11.15's "an inactive-but-referenced
 * value must still be selectable/visible").
 */
export type LabelResolver = (key: ListFilterKey, value: string) => string | undefined;

/**
 * The i18n key each dimension's micro-label resolves through (R18.9), matching
 * the filter bar's control labels. Every key lives under `filter.dimension.*` in
 * the externalized catalogue; no dimension label is inlined here.
 */
const LIST_DIMENSION_LABEL_KEYS: Record<ListFilterKey, string> = {
  salesCycleIds: 'filter.dimension.salesCycleIds',
  months: 'filter.dimension.months',
  isoWeeks: 'filter.dimension.isoWeeks',
  platformIds: 'filter.dimension.platformIds',
  channelIds: 'filter.dimension.channelIds',
  customerGroupIds: 'filter.dimension.customerGroupIds',
  audienceIds: 'filter.dimension.audienceIds',
  categoryIds: 'filter.dimension.categoryIds',
  initiativeTypeIds: 'filter.dimension.initiativeTypeIds',
  ownerUserIds: 'filter.dimension.ownerUserIds',
  statuses: 'filter.dimension.statuses',
  priorityIds: 'filter.dimension.priorityIds',
  fieldForceRoles: 'filter.dimension.fieldForceRoles',
  // `complan-governance` R6.11, R6.12. The calendar renders no control for these
  // two, but a Filter_Context arriving from a dashboard carries them (R6.3), and a
  // dimension that is applied and has no chip is a filter the user cannot see or
  // remove - so a chip is exactly what they need.
  watchoutSeverities: 'filter.dimension.watchoutSeverities',
  milestoneReadiness: 'filter.dimension.milestoneReadiness',
};

const BOOLEAN_DIMENSION_LABEL_KEYS: Record<BooleanFilterKey, string> = {
  confidential: 'filter.dimension.confidential',
  fieldForceInvolvement: 'filter.dimension.fieldForceInvolvement',
  fieldForceMaterial: 'filter.dimension.fieldForceMaterial',
  hasWorkOrder: 'filter.dimension.hasWorkOrder',
  hasInformationContribution: 'filter.dimension.hasInformationContribution',
  dteInvolvement: 'filter.dimension.dteInvolvement',
  cscInvolvement: 'filter.dimension.cscInvolvement',
  openDependencies: 'filter.dimension.openDependencies',
  // See the note on the two governance list dimensions above.
  hasUnresolvedWatchout: 'filter.dimension.hasUnresolvedWatchout',
};

/**
 * The label resolvers, defaulting to the shared app catalogue so a caller (and a
 * unit test) that supplies no translator still gets English text from the single
 * source rather than an inlined string. A React component passes its own `t`.
 */
function defaultT(): TFunction {
  return i18n.t.bind(i18n) as TFunction;
}

/** The display label for a list dimension, from the catalogue (R18.9). */
export function listDimensionLabel(key: ListFilterKey, t: TFunction = defaultT()): string {
  return t(LIST_DIMENSION_LABEL_KEYS[key]);
}

/** The display label for a Boolean dimension, from the catalogue (R18.9). */
export function booleanDimensionLabel(key: BooleanFilterKey, t: TFunction = defaultT()): string {
  return t(BOOLEAN_DIMENSION_LABEL_KEYS[key]);
}

/**
 * The result-context chip's text (R11.9), from the catalogue with i18next
 * pluralisation. Correct at zero — the count is shown on every apply, change or
 * clear, the zero included, because an empty result is a legitimate answer the
 * user must see stated rather than infer from a blank calendar (R10.16).
 */
export function resultCountChipLabel(count: number, t: TFunction = defaultT()): string {
  return t('filter.resultCountVisible', { count });
}

/**
 * Derive the chip row for a query and its result count.
 *
 * The filter chips come first, in the filter bar's control order (list dimensions
 * then Boolean dimensions), one chip per value of a multi-value dimension so each
 * is independently removable. The mandatory result-count chip is appended last and
 * is always present.
 */
export function deriveChips(
  query: CalendarQuery,
  count: number,
  resolveLabel?: LabelResolver,
  t: TFunction = defaultT(),
): FilterChip[] {
  const chips: FilterChip[] = [];

  for (const key of LIST_KEYS) {
    const values = query[key];
    for (const value of values) {
      const stringValue = String(value);
      const resolved = resolveLabel?.(key, stringValue) ?? stringValue;
      chips.push({
        key: `${key}:${stringValue}`,
        label: t('filter.chip.value', { label: listDimensionLabel(key, t), value: resolved }),
        removal: { type: 'list-value', key, value: stringValue },
        kind: 'filter',
      });
    }
  }

  for (const key of BOOLEAN_KEYS) {
    const value = query[key];
    if (value !== null) {
      chips.push({
        key,
        label: t('filter.chip.value', {
          label: booleanDimensionLabel(key, t),
          value: value ? t('filter.booleanYes') : t('filter.booleanNo'),
        }),
        removal: { type: 'boolean', key },
        kind: 'filter',
      });
    }
  }

  // The free-text Search term, shown last of the filter chips because it is the
  // last control in the bar (UI spec §3.6's ordering).
  if (query.search !== null) {
    chips.push({
      key: 'search',
      label: t('filter.chip.value', {
        label: t('filter.dimension.search'),
        value: query.search,
      }),
      removal: { type: 'search' },
      kind: 'filter',
    });
  }

  // The mandatory result-context chip terminates the row (R11.9, UI spec §3.7).
  chips.push({
    key: 'result-count',
    label: resultCountChipLabel(count, t),
    kind: 'result',
  });

  return chips;
}

/** Whether any filter dimension is applied (used to enable/disable the Reset control). */
export function hasActiveFilters(query: CalendarQuery): boolean {
  for (const key of LIST_KEYS) {
    if (query[key].length > 0) {
      return true;
    }
  }
  for (const key of BOOLEAN_KEYS) {
    if (query[key] !== null) {
      return true;
    }
  }
  return query.search !== null;
}

export { LIST_DIMENSION_LABEL_KEYS, BOOLEAN_DIMENSION_LABEL_KEYS };
